import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type Pitch,
  type ScaleType,
  type ChordQuality,
  type KeyMode,
  type ProgressionTemplate,
  type Letter,
  type Accidental,
  PROGRESSION_TEMPLATES,
  key,
  pitch,
  pitchToString,
  scale,
  scalePitches,
  chord,
  chordPitches,
  toMidi,
  scaleSolfege,
  realizeProgression,
} from "@music-app/theory-engine";
import {
  createAudioPlayer,
  type AudioPlayer,
} from "@music-app/audio-engine-web";
import { AudioStatusIndicator } from "@music-app/ui-components";
import {
  Score,
  scaleToVexLine,
  chordToVexChord,
  pitchToVexKey,
  type VexNote,
} from "@music-app/notation";
import {
  scalePlan,
  chordPlan,
  intervalPlan,
  progressionPlan,
  LIBRARY_INTERVALS,
} from "../lib/library-audio.js";
import { PageHeader } from "../components/PageHeader.js";

type Tab = "scales" | "chords" | "intervals" | "progressions";

type AudioStatus = "loading" | "ready" | "synth-fallback" | "error";

interface RootChoice {
  letter: Letter;
  accidental: Accidental;
  label: string;
}

const ROOTS: RootChoice[] = [
  { letter: "C", accidental: 0, label: "C" },
  { letter: "D", accidental: -1, label: "D♭" },
  { letter: "D", accidental: 0, label: "D" },
  { letter: "E", accidental: -1, label: "E♭" },
  { letter: "E", accidental: 0, label: "E" },
  { letter: "F", accidental: 0, label: "F" },
  { letter: "F", accidental: 1, label: "F♯" },
  { letter: "G", accidental: 0, label: "G" },
  { letter: "A", accidental: -1, label: "A♭" },
  { letter: "A", accidental: 0, label: "A" },
  { letter: "B", accidental: -1, label: "B♭" },
  { letter: "B", accidental: 0, label: "B" },
];

interface ScaleEntry {
  type: ScaleType;
  display: string;
  group: "modes" | "diatonic" | "pentatonic" | "blues" | "exotic" | "symmetric";
}

const SCALE_CATALOG: ScaleEntry[] = [
  { type: "major",             display: "Major (Ionian)",        group: "diatonic" },
  { type: "natural-minor",     display: "Natural Minor (Aeolian)", group: "diatonic" },
  { type: "harmonic-minor",    display: "Harmonic Minor",        group: "diatonic" },
  { type: "melodic-minor",     display: "Melodic Minor",         group: "diatonic" },
  { type: "dorian",            display: "Dorian",                group: "modes" },
  { type: "phrygian",          display: "Phrygian",              group: "modes" },
  { type: "lydian",            display: "Lydian",                group: "modes" },
  { type: "mixolydian",        display: "Mixolydian",            group: "modes" },
  { type: "locrian",           display: "Locrian",               group: "modes" },
  { type: "major-pentatonic",  display: "Major Pentatonic",      group: "pentatonic" },
  { type: "minor-pentatonic",  display: "Minor Pentatonic",      group: "pentatonic" },
  { type: "blues",             display: "Blues",                 group: "blues" },
  { type: "lydian-dominant",   display: "Lydian Dominant",       group: "exotic" },
  { type: "phrygian-dominant", display: "Phrygian Dominant",     group: "exotic" },
  { type: "altered",           display: "Altered (Super Locrian)", group: "exotic" },
  { type: "whole-tone",        display: "Whole Tone",            group: "symmetric" },
  { type: "diminished-wh",     display: "Diminished (W-H)",      group: "symmetric" },
  { type: "diminished-hw",     display: "Diminished (H-W)",      group: "symmetric" },
];

interface ChordEntry {
  quality: ChordQuality;
  display: string;
  group: "triad" | "seventh" | "sus";
}

const CHORD_CATALOG: ChordEntry[] = [
  { quality: "maj",     display: "Major",              group: "triad" },
  { quality: "min",     display: "Minor",              group: "triad" },
  { quality: "dim",     display: "Diminished",         group: "triad" },
  { quality: "aug",     display: "Augmented",          group: "triad" },
  { quality: "sus2",    display: "Suspended 2",        group: "sus" },
  { quality: "sus4",    display: "Suspended 4",        group: "sus" },
  { quality: "maj7",    display: "Major 7",            group: "seventh" },
  { quality: "min7",    display: "Minor 7",            group: "seventh" },
  { quality: "dom7",    display: "Dominant 7",         group: "seventh" },
  { quality: "min7b5",  display: "Half-diminished 7",  group: "seventh" },
  { quality: "dim7",    display: "Diminished 7",       group: "seventh" },
];

const PROGRESSION_KEYS = Object.keys(PROGRESSION_TEMPLATES);

export function LibraryPage(): ReactNode {
  const [tab, setTab] = useState<Tab>("scales");
  const [rootIndex, setRootIndex] = useState(0);
  const [progressionMode, setProgressionMode] = useState<KeyMode>("major");
  const [audioStatus, setAudioStatus] = useState<AudioStatus>("loading");
  const [pianoProgress, setPianoProgress] = useState<number | undefined>(undefined);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const playerRef = useRef<AudioPlayer | null>(null);
  // Token used to invalidate in-flight playback when a new entry is clicked.
  const playTokenRef = useRef(0);

  const root: Pitch = useMemo(() => {
    const r = ROOTS[rootIndex]!;
    return pitch(r.letter, r.accidental, 4);
  }, [rootIndex]);

  useEffect(() => {
    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, []);

  const ensureAudio = useCallback(async (): Promise<AudioPlayer> => {
    if (playerRef.current) return playerRef.current;
    const p = createAudioPlayer();
    await p.init();
    playerRef.current = p;
    setAudioStatus("synth-fallback");
    setPianoProgress(0);
    void p
      .loadInstrument("piano", {
        onProgress: (loaded, total) => {
          setPianoProgress(total > 0 ? loaded / total : 0);
        },
      })
      .then(() => {
        try {
          p.setInstrument("piano");
          setAudioStatus("ready");
          setPianoProgress(undefined);
        } catch {
          setAudioStatus("synth-fallback");
        }
      })
      .catch(() => {
        setAudioStatus("synth-fallback");
        setPianoProgress(undefined);
      });
    return p;
  }, []);

  const play = useCallback(
    async (entryId: string, planFactory: () => ReturnType<typeof scalePlan>) => {
      const p = await ensureAudio();
      const token = ++playTokenRef.current;
      // Stop whatever's playing — clicking another entry should interrupt.
      p.stop();
      setPlayingId(entryId);
      try {
        await p.play(planFactory());
      } catch {
        // ignored — stop() rejects play
      }
      // Only clear the indicator if no newer click superseded us.
      if (playTokenRef.current === token) {
        setPlayingId(null);
      }
    },
    [ensureAudio],
  );

  return (
    <div className="max-w-3xl mx-auto py-9 px-8">
      <PageHeader
        eyebrow="reference"
        title="Theory library"
        emphasis="library"
        subhead="Scales, chords, intervals, and progressions — all playable. Pick a root, then click any row to hear it."
        right={
          <AudioStatusIndicator
            status={audioStatus}
            progress={pianoProgress}
            instrumentLabel={
              audioStatus === "ready" ? "piano" : audioStatus === "synth-fallback" ? "synth" : undefined
            }
          />
        }
      />

      <RootSelector value={rootIndex} onChange={setRootIndex} />

      <TabBar active={tab} onChange={setTab} />

      <div className="mt-6">
        {tab === "scales" && (
          <ScalesPanel root={root} playingId={playingId} onPlay={play} />
        )}
        {tab === "chords" && (
          <ChordsPanel root={root} playingId={playingId} onPlay={play} />
        )}
        {tab === "intervals" && (
          <IntervalsPanel root={root} playingId={playingId} onPlay={play} />
        )}
        {tab === "progressions" && (
          <ProgressionsPanel
            root={root}
            mode={progressionMode}
            onModeChange={setProgressionMode}
            playingId={playingId}
            onPlay={play}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Root selector ───────────────────────────────────────────────────────── */

interface RootSelectorProps {
  value: number;
  onChange: (v: number) => void;
}

function RootSelector({ value, onChange }: RootSelectorProps): ReactNode {
  return (
    <div className="mb-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">root</p>
      <div role="radiogroup" aria-label="Root note" className="flex flex-wrap gap-1">
        {ROOTS.map((r, i) => {
          const selected = i === value;
          return (
            <button
              key={r.label}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(i)}
              className={`min-w-[40px] py-1.5 px-2 text-sm border transition-colors tabular-nums ${
                selected
                  ? "bg-ink text-paper border-ink"
                  : "bg-paper text-ink border-ink/20 hover:bg-paperWarm"
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Tab bar ─────────────────────────────────────────────────────────────── */

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "scales", label: "Scales" },
  { id: "chords", label: "Chords" },
  { id: "intervals", label: "Intervals" },
  { id: "progressions", label: "Progressions" },
];

interface TabBarProps {
  active: Tab;
  onChange: (t: Tab) => void;
}

function TabBar({ active, onChange }: TabBarProps): ReactNode {
  // Segmented-control pattern (button group with aria-pressed). The
  // panels don't carry tabpanel semantics either, so this is a button
  // group that visually resembles tabs — accurate ARIA rather than
  // borrowed ARIA.
  return (
    <div role="group" aria-label="Library section" className="flex gap-1">
      {TABS.map((t) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(t.id)}
            className={`py-2 px-4 text-sm font-mono uppercase tracking-[0.18em] transition-colors ${
              selected
                ? "bg-ink text-paper"
                : "text-inkFaded border border-ink/20 hover:text-ink hover:bg-paperWarm"
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Entry row ───────────────────────────────────────────────────────────── */

interface EntryRowProps {
  id: string;
  title: ReactNode;
  detail?: ReactNode;
  meta?: ReactNode;
  isPlaying: boolean;
  onPlay: () => void;
}

function EntryRow({ id, title, detail, meta, isPlaying, onPlay }: EntryRowProps): ReactNode {
  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label={`Play ${typeof title === "string" ? title : id}`}
      className={`w-full text-left flex items-center gap-4 py-3 px-4 border-b border-ink/10 transition-colors ${
        isPlaying ? "bg-accent/10" : "hover:bg-paperWarm"
      }`}
    >
      <span
        className={`w-8 h-8 flex items-center justify-center border ${
          isPlaying ? "border-accent text-accent" : "border-ink/20 text-ink"
        }`}
        aria-hidden
      >
        {isPlaying ? (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
            <rect x="1" y="1" width="3" height="8" />
            <rect x="6" y="1" width="3" height="8" />
          </svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
            <polygon points="2,1 9,5 2,9" />
          </svg>
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-base text-ink truncate">{title}</div>
        {detail && (
          <div className="font-mono text-[11px] text-inkFaded mt-0.5 truncate">{detail}</div>
        )}
      </div>
      {meta && <div className="font-mono text-xs text-inkFaded tabular-nums">{meta}</div>}
    </button>
  );
}

/* ─── Section header ──────────────────────────────────────────────────────── */

function SectionHeader({ children }: { children: ReactNode }): ReactNode {
  return (
    <h2 className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent px-4 pt-4 pb-2">
      {children}
    </h2>
  );
}

/* ─── Notation helpers ────────────────────────────────────────────────────── */

/** Flat-biased semitone → VexFlow letter string (e.g. 1 → "db", 6 → "f#"). */
const MIDI_SEMI_TO_VEX = ["c", "db", "d", "eb", "e", "f", "f#", "g", "ab", "a", "bb", "b"] as const;

/** Raise a VexFlow key string by one octave: "c/4" → "c/5". */
function raiseOctave(key: string): string {
  return key.replace(/\/(\d+)$/, (_, oct) => `/${String(Number(oct) + 1)}`);
}

/** Convert a MIDI number to a flat-biased VexFlow key string. */
function midiToVexKey(midi: number): string {
  const semi = ((midi % 12) + 12) % 12;
  const oct = Math.floor(midi / 12) - 1;
  return `${MIDI_SEMI_TO_VEX[semi]!}/${oct}`;
}

/* ─── Notation panel ──────────────────────────────────────────────────────── */

interface NotationPanelProps {
  notes: VexNote[] | null;
  ariaLabel: string;
  width?: number;
  height?: number;
}

function NotationPanel({ notes, ariaLabel, width = 500, height = 150 }: NotationPanelProps): ReactNode {
  if (!notes) return null;
  return (
    <div className="border-b border-ink/10 bg-paper overflow-hidden">
      <Score notes={notes} width={width} height={height} aria-label={ariaLabel} />
    </div>
  );
}

/* ─── Scales panel ────────────────────────────────────────────────────────── */

interface ScalesPanelProps {
  root: Pitch;
  playingId: string | null;
  onPlay: (id: string, factory: () => ReturnType<typeof scalePlan>) => void;
}

function ScalesPanel({ root, playingId, onPlay }: ScalesPanelProps): ReactNode {
  const [selectedType, setSelectedType] = useState<ScaleType>(
    SCALE_CATALOG[0]!.type,
  );

  const grouped = useMemo(() => {
    const groups: Record<ScaleEntry["group"], ScaleEntry[]> = {
      diatonic: [],
      modes: [],
      pentatonic: [],
      blues: [],
      exotic: [],
      symmetric: [],
    };
    for (const e of SCALE_CATALOG) groups[e.group].push(e);
    return groups;
  }, []);

  const scoreNotes = useMemo((): VexNote[] => {
    const sc = scale(root, selectedType);
    const notes = scaleToVexLine(sc);
    const firstKey = notes[0]?.keys[0];
    if (!firstKey) return notes;
    return [...notes, { keys: [raiseOctave(firstKey)], duration: "q" }];
  }, [root, selectedType]);

  const selectedEntry = SCALE_CATALOG.find((e) => e.type === selectedType);
  const rootName = stripOctave(pitchToString(root));
  const scoreLabel = selectedEntry
    ? `${rootName} ${selectedEntry.display} scale, ascending`
    : `${rootName} scale, ascending`;

  const labels: Record<ScaleEntry["group"], string> = {
    diatonic: "Diatonic & Minor",
    modes: "Modes of Major",
    pentatonic: "Pentatonic",
    blues: "Blues",
    exotic: "Exotic",
    symmetric: "Symmetric",
  };

  return (
    <div className="border border-ink/10">
      <NotationPanel notes={scoreNotes} ariaLabel={scoreLabel} width={500} />
      {(Object.keys(grouped) as ScaleEntry["group"][]).map((g) => {
        const entries = grouped[g];
        if (entries.length === 0) return null;
        return (
          <div key={g}>
            <SectionHeader>{labels[g]}</SectionHeader>
            {entries.map((e) => {
              const id = `scale:${e.type}`;
              return (
                <ScaleRow
                  key={e.type}
                  id={id}
                  entry={e}
                  root={root}
                  isPlaying={playingId === id}
                  onPlay={() => {
                    setSelectedType(e.type);
                    onPlay(id, () => scalePlan(root, e.type));
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

interface ScaleRowProps {
  id: string;
  entry: ScaleEntry;
  root: Pitch;
  isPlaying: boolean;
  onPlay: () => void;
}

function ScaleRow({ id, entry, root, isPlaying, onPlay }: ScaleRowProps): ReactNode {
  const sc = scale(root, entry.type);
  const noteNames = scalePitches(sc).map((p) => stripOctave(pitchToString(p)));
  const isMinor = entry.type === "natural-minor"
    || entry.type === "harmonic-minor"
    || entry.type === "melodic-minor";
  // Solfège annotation only meaningful for diatonic 7-note scales.
  const showSolfege = noteNames.length === 7;
  const solfege = showSolfege ? scaleSolfege(sc, isMinor) : null;
  return (
    <EntryRow
      id={id}
      title={`${stripOctave(pitchToString(root))} ${entry.display}`}
      detail={
        solfege
          ? `${noteNames.join(" ")} · ${solfege.join(" ")}`
          : noteNames.join(" ")
      }
      meta={`${noteNames.length} notes`}
      isPlaying={isPlaying}
      onPlay={onPlay}
    />
  );
}

/* ─── Chords panel ────────────────────────────────────────────────────────── */

interface ChordsPanelProps {
  root: Pitch;
  playingId: string | null;
  onPlay: (id: string, factory: () => ReturnType<typeof scalePlan>) => void;
}

function ChordsPanel({ root, playingId, onPlay }: ChordsPanelProps): ReactNode {
  const [selectedQuality, setSelectedQuality] = useState<ChordQuality>(
    CHORD_CATALOG[0]!.quality,
  );

  const grouped: Record<ChordEntry["group"], ChordEntry[]> = {
    triad: [],
    sus: [],
    seventh: [],
  };
  for (const e of CHORD_CATALOG) grouped[e.group].push(e);

  const scoreNotes = useMemo((): VexNote[] => {
    const c = chord(root, selectedQuality);
    return [chordToVexChord(c)];
  }, [root, selectedQuality]);

  const selectedEntry = CHORD_CATALOG.find((e) => e.quality === selectedQuality);
  const rootName = stripOctave(pitchToString(root));
  const scoreLabel = selectedEntry
    ? `${rootName} ${selectedEntry.display} chord`
    : `${rootName} chord`;

  const labels: Record<ChordEntry["group"], string> = {
    triad: "Triads",
    sus: "Suspended",
    seventh: "Seventh chords",
  };

  return (
    <div className="border border-ink/10">
      <NotationPanel notes={scoreNotes} ariaLabel={scoreLabel} width={240} />
      {(["triad", "sus", "seventh"] as ChordEntry["group"][]).map((g) => (
        <div key={g}>
          <SectionHeader>{labels[g]}</SectionHeader>
          {grouped[g].map((e) => {
            const id = `chord:${e.quality}`;
            const c = chord(root, e.quality);
            const noteNames = chordPitches(c).map((p) => stripOctave(pitchToString(p)));
            return (
              <EntryRow
                key={e.quality}
                id={id}
                title={c.symbol === "" ? `${stripOctave(pitchToString(root))}` : c.symbol}
                detail={`${e.display} · ${noteNames.join(" ")}`}
                meta={`${noteNames.length} notes`}
                isPlaying={playingId === id}
                onPlay={() => {
                  setSelectedQuality(e.quality);
                  onPlay(id, () => chordPlan(root, e.quality));
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ─── Intervals panel ─────────────────────────────────────────────────────── */

interface IntervalsPanelProps {
  root: Pitch;
  playingId: string | null;
  onPlay: (id: string, factory: () => ReturnType<typeof scalePlan>) => void;
}

function IntervalsPanel({ root, playingId, onPlay }: IntervalsPanelProps): ReactNode {
  const [selectedSemitones, setSelectedSemitones] = useState<number>(
    LIBRARY_INTERVALS[0]!.semitones,
  );

  const rootMidi = toMidi(root);

  const scoreNotes = useMemo((): VexNote[] => {
    const rootKey = pitchToVexKey(root);
    const upperMidi = rootMidi + selectedSemitones;
    const upperKey = midiToVexKey(upperMidi);
    if (selectedSemitones === 0) {
      return [{ keys: [rootKey], duration: "q" }];
    }
    return [
      { keys: [rootKey], duration: "q" },
      { keys: [upperKey], duration: "q" },
    ];
  }, [root, rootMidi, selectedSemitones]);

  const selectedInterval = LIBRARY_INTERVALS.find(
    (iv) => iv.semitones === selectedSemitones,
  );
  const rootName = stripOctave(pitchToString(root));
  const scoreLabel = selectedInterval
    ? `${selectedInterval.label} (${selectedInterval.longName}) from ${rootName}`
    : `Interval from ${rootName}`;

  return (
    <div className="border border-ink/10">
      <NotationPanel notes={scoreNotes} ariaLabel={scoreLabel} width={280} />
      <SectionHeader>Ascending from {stripOctave(pitchToString(root))}</SectionHeader>
      {LIBRARY_INTERVALS.map((iv) => {
        const id = `interval:${iv.label}`;
        return (
          <EntryRow
            key={iv.label}
            id={id}
            title={`${iv.label} · ${iv.longName}`}
            detail={`${iv.semitones} semitone${iv.semitones === 1 ? "" : "s"}`}
            meta={iv.label}
            isPlaying={playingId === id}
            onPlay={() => {
              setSelectedSemitones(iv.semitones);
              onPlay(id, () => intervalPlan(rootMidi, iv.semitones));
            }}
          />
        );
      })}
    </div>
  );
}

/* ─── Progressions panel ──────────────────────────────────────────────────── */

interface ProgressionsPanelProps {
  root: Pitch;
  mode: KeyMode;
  onModeChange: (m: KeyMode) => void;
  playingId: string | null;
  onPlay: (id: string, factory: () => ReturnType<typeof scalePlan>) => void;
}

function ProgressionsPanel({
  root,
  mode,
  onModeChange,
  playingId,
  onPlay,
}: ProgressionsPanelProps): ReactNode {
  const [selectedProgKey, setSelectedProgKey] = useState<string | null>(
    PROGRESSION_KEYS[0] ?? null,
  );

  const k = useMemo(() => key(root, mode), [root, mode]);

  const scoreNotes = useMemo((): VexNote[] | null => {
    if (!selectedProgKey) return null;
    const tpl = PROGRESSION_TEMPLATES[selectedProgKey];
    if (!tpl) return null;
    const chords = realizeProgression(tpl, k);
    return chords.map((c) => chordToVexChord(c));
  }, [selectedProgKey, k]);

  const selectedTpl = selectedProgKey ? PROGRESSION_TEMPLATES[selectedProgKey] : null;
  const scoreLabel = selectedTpl
    ? `${selectedTpl.name} in ${k.name}`
    : `Progression in ${k.name}`;

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mr-2">mode</p>
        {(["major", "minor"] as KeyMode[]).map((m) => {
          const selected = mode === m;
          return (
            <button
              key={m}
              type="button"
              aria-pressed={selected}
              onClick={() => onModeChange(m)}
              className={`py-1.5 px-3 text-sm border transition-colors ${
                selected
                  ? "bg-ink text-paper border-ink"
                  : "bg-paper text-ink border-ink/20 hover:bg-paperWarm"
              }`}
            >
              {m === "major" ? "Major" : "Minor"}
            </button>
          );
        })}
        <span className="ml-auto font-mono text-xs text-inkFaded">in {k.name}</span>
      </div>
      <div className="border border-ink/10">
        <NotationPanel notes={scoreNotes} ariaLabel={scoreLabel} width={520} />
        {PROGRESSION_KEYS.map((tplKey) => {
          const tpl = PROGRESSION_TEMPLATES[tplKey];
          if (!tpl) return null;
          const id = `progression:${tplKey}:${mode}`;
          return (
            <ProgressionRow
              key={tplKey}
              id={id}
              template={tpl}
              k={k}
              isPlaying={playingId === id}
              onPlay={() => {
                setSelectedProgKey(tplKey);
                onPlay(id, () => progressionPlan(tpl, k));
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ProgressionRowProps {
  id: string;
  template: ProgressionTemplate;
  k: ReturnType<typeof key>;
  isPlaying: boolean;
  onPlay: () => void;
}

function ProgressionRow({ id, template, k, isPlaying, onPlay }: ProgressionRowProps): ReactNode {
  return (
    <EntryRow
      id={id}
      title={template.name}
      detail={`${template.label} · in ${k.name}`}
      meta={`${template.steps.length} chords`}
      isPlaying={isPlaying}
      onPlay={onPlay}
    />
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

/** "C4" → "C", "Bb3" → "Bb". The library is octave-agnostic in display. */
function stripOctave(s: string): string {
  return s.replace(/-?\d+$/, "");
}
